import { useMemo } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { getColumn, getColumnNames } from '../arrow/arqueroHelpers'

export interface CellEmbeddings {
  /** Whether embedding columns were found in the session data */
  available: boolean
  /** Embedding dimensionality */
  dim: number
  /** Row-major, L2-normalized matrix of shape (rowCount x dim) */
  matrix: Float32Array
  /** cellId -> row index */
  rowByCellId: Map<string, number>
  /** row index -> cellId */
  cellIdByRow: string[]
}

const EMPTY: CellEmbeddings = {
  available: false,
  dim: 0,
  matrix: new Float32Array(0),
  rowByCellId: new Map(),
  cellIdByRow: [],
}

/**
 * Builds an L2-normalized embedding matrix from the loaded session dataframe.
 * Embedding dimensions are the integer-named columns ('0','1',...), which is
 * how the HFM features are stored. Built once per dataframe.
 */
export const useCellEmbeddings = (): CellEmbeddings => {
  const { dataFrame } = useCellVisualizationsSlice()

  return useMemo<CellEmbeddings>(() => {
    if (!dataFrame || dataFrame.numRows() === 0) return EMPTY

    const columnNames = getColumnNames(dataFrame)
      .filter((name) => /^\d+$/.test(name))
      .sort((a, b) => Number(a) - Number(b))
    if (columnNames.length === 0) return EMPTY

    const dim = columnNames.length
    const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
    const rowCount = cellIds.length
    const columns = columnNames.map((name) => getColumn<number | string>(dataFrame, name))

    const matrix = new Float32Array(rowCount * dim)
    const rowByCellId = new Map<string, number>()

    for (let row = 0; row < rowCount; row += 1) {
      const base = row * dim
      let norm = 0
      for (let c = 0; c < dim; c += 1) {
        const value = Number(columns[c][row]) || 0
        matrix[base + c] = value
        norm += value * value
      }
      norm = Math.sqrt(norm)
      if (norm > 0) {
        for (let c = 0; c < dim; c += 1) matrix[base + c] /= norm
      }
      rowByCellId.set(cellIds[row], row)
    }

    return { available: true, dim, matrix, rowByCellId, cellIdByRow: cellIds }
  }, [dataFrame])
}

export default useCellEmbeddings
